// Centralized API utility for all HTTP requests
// Uses the centralized configuration

import axios from 'axios';
import config from '../config/config';

// Create axios instance with centralized config
const api = axios.create({
  baseURL: config.api.baseURL,  // Uses the simple boolean flag from config
  timeout: config.api.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for adding auth tokens, logging, etc.
api.interceptors.request.use(
  (config) => {
    // Log API calls in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`🌐 API Request: ${config.method?.toUpperCase()} ${config.url}`);
    }

    // Add JWT token if available
    const token = localStorage.getItem('jwt_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Refresh-token coordination. A single in-flight refresh is shared across
// every request that races into 401/403 — avoids a thundering herd of
// concurrent refresh calls when the user lands on a page that fires several
// requests at once.
let refreshPromise = null;
async function refreshTokenOnce() {
  if (refreshPromise) return refreshPromise;
  const oldToken = localStorage.getItem('jwt_token');
  if (!oldToken) return null;

  refreshPromise = axios
    .post(
      `${config.api.baseURL}/api/auth/refresh`,
      {},
      { headers: { Authorization: `Bearer ${oldToken}` } }
    )
    .then((res) => {
      if (res.data?.success && res.data?.token) {
        localStorage.setItem('jwt_token', res.data.token);
        return res.data.token;
      }
      return null;
    })
    .catch(() => null)
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

function clearAndRedirect() {
  localStorage.removeItem('jwt_token');
  localStorage.removeItem('user_data');
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

// Response interceptor for error handling, logging, etc.
api.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`);
    }
    return response;
  },
  async (error) => {
    const status = error.response?.status;
    const original = error.config;

    console.error('API Response Error:', {
      url: original?.url,
      method: original?.method,
      status,
      message: error.message,
      data: error.response?.data,
    });

    // Auth recovery: a single 401/403 should try refresh+retry before
    // bouncing the user to /login. Skip for refresh/login endpoints
    // themselves so we never loop, and skip if we've already retried this
    // request once.
    const isAuthEndpoint =
      original?.url?.includes('/api/auth/refresh') ||
      original?.url?.includes('/api/auth/login');

    if ((status === 401 || status === 403) && !original?._retry && !isAuthEndpoint) {
      original._retry = true;
      const newToken = await refreshTokenOnce();
      if (newToken) {
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      }
      // Refresh couldn't recover — boot to login.
      clearAndRedirect();
    }

    if (status >= 500) {
      console.error('Server error - consider showing user-friendly message');
    }
    if (error.code === 'ECONNABORTED') {
      console.error('Request timeout - consider retrying');
    }

    return Promise.reject(error);
  }
);

// Retry logic for failed requests (exported for potential external use)
// eslint-disable-next-line no-unused-vars
const retryRequest = async (originalRequest, retryCount = 0) => {
  if (retryCount >= config.api.retryAttempts) {
    throw new Error('Max retry attempts reached');
  }

  // Wait before retrying
  await new Promise(resolve => setTimeout(resolve, config.api.retryDelay * (retryCount + 1)));

  try {
    return await api(originalRequest);
  } catch (error) {
    if (error.response?.status >= 500 || error.code === 'ECONNABORTED') {
      return retryRequest(originalRequest, retryCount + 1);
    }
    throw error;
  }
};

// API helper functions
export const apiHelpers = {
  // GET request with optional params
  get: (endpoint, params = {}) => {
    return api.get(endpoint, { params });
  },

  // POST request
  post: (endpoint, data = {}) => {
    return api.post(endpoint, data);
  },

  // PUT request
  put: (endpoint, data = {}) => {
    return api.put(endpoint, data);
  },

  // DELETE request
  delete: (endpoint) => {
    return api.delete(endpoint);
  },

  // Upload file
  upload: (endpoint, formData) => {
    return api.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Download file
  download: (endpoint, filename) => {
    return api.get(endpoint, {
      responseType: 'blob',
    }).then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    });
  }
};

// Export the configured axios instance
export default api;

// Export config for components that need it
export { config };
